import { useState, useEffect } from "react";
import { Participant } from "./api/Dtos";
import { DELETE, PUT } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import {
  Button,
  Center,
  HStack,
  Input,
  Select,
  Tag,
  TagLabel,
  Text,
  VStack,
} from "@chakra-ui/react";

import { FastTable, isNumber } from "./Table/FastTable";
import {
  downloadExcel,
  useParticipantsFromBackend,
} from "./Services/ParticipantService";
import { timeInMsToString } from "./Services/ResultService";

const disableModifying = false; // Disabled editing for now as we use ofi homepage for stadsloppet-2025

const fixedFields = [
  "startnumber",
  "disciplineName",
  //"startgroup",
  "firstname",
  "lastname",
  "birthYear",
  "clubOrTeam",
  //"dns", // Did not start
  //"dnf", // Did not finish
  "gender",
];

const fixedDisplayLabels = [
  "Startnummer",
  "Klass",
  //"Startgrupp",
  "Förnamn",
  "Efternamn",
  "Född",
  "Klubb/Lag",
  //"DNS",
  //"DNF",
  "Kön (man/kvinna)",
];
const modifyFields = [
  //"startnumber",
  //"klass",
  "startgroupId",
  //"firstname",
  //"lastname",
  //"birthYear",
  //"clubOrTeam",
  //"dns", // Did not start
  //"dnf", // Did not finish
  //"gender",
];

const modifyDisplayLabels = [
  //"Startnummer",
  //"Klass",
  "Startgrupp",
  //"Förnamn",
  //"Efternamn",
  //"Född",
  //"Klubb/Lag",
  //"DNS",
  //"DNF",
  //"Kön (man/kvinna)",
];

export function Participants() {
  const globalContext = useGlobalContext();

  const { participants, setParticipants, loading } =
    useParticipantsFromBackend();

  const [filterClass, setFilterClass] = useState<string | undefined>(undefined);
  const [filterStartgroupId, setFilterStartgroupId] = useState<
    number | undefined
  >(undefined);
  const [filterStartnrOrName, setFilterStartnrOrName] = useState<
    string | undefined
  >(undefined);
  const [filterGroupName, setFilterGroupName] = useState<string | undefined>(
    undefined
  );

  const [filteredParticipants, setFilteredParticipants] = useState<
    Array<Participant>
  >([]);

  // Apply filtering
  useEffect(() => {
    let newParticipantArray = [...participants];
    if (filterClass) {
      newParticipantArray = newParticipantArray.filter(
        (p) => p.disciplineName === filterClass
      );
    }

    if (filterStartgroupId) {
      newParticipantArray = newParticipantArray.filter(
        (p) => p.startgroupId === filterStartgroupId
      );
    }

    if (filterStartnrOrName) {
      if (isNumber(filterStartnrOrName)) {
        newParticipantArray = newParticipantArray.filter((p) => {
          return `${p.startnumber}`.startsWith(filterStartnrOrName);
        });
      } else {
        newParticipantArray = newParticipantArray.filter((p) =>
          `${p.firstname} ${p.lastname}`.includes(filterStartnrOrName)
        );
      }
    }

    if (filterGroupName) {
      newParticipantArray = newParticipantArray.filter((p) =>
        `${p.clubOrTeam}`.includes(filterGroupName)
      );
    }

    console.log(
      "filtered:",
      newParticipantArray,
      globalContext.competition?.startgroups
    );

    setFilteredParticipants(newParticipantArray);
  }, [
    participants,
    filterClass,
    filterStartgroupId,
    filterStartnrOrName,
    filterGroupName,
  ]);

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        <VStack>
          <Text>Antal</Text>
          <Tag
            size="lg"
            colorScheme="gray"
            borderRadius="10px"
            borderWidth={"2px"}
            borderColor={"black"}
          >
            <TagLabel>
              {filteredParticipants.length === participants.length
                ? participants.length
                : `${filteredParticipants.length} / ${participants.length}`}
            </TagLabel>
          </Tag>
        </VStack>
        <VStack>
          <Text>Välj klass</Text>
          <Select
            minW={100}
            placeholder="Alla"
            onChange={(event) => {
              if (filterClass === event.currentTarget.value) {
                setFilterClass(undefined);
                console.log("unset filterclass");
              } else {
                setFilterClass(event.currentTarget.value);
                console.log("set filterclass to ", event.currentTarget.value);
              }
            }}
          >
            {globalContext.competition?.disciplines.map((c) => {
              return (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              );
            })}
          </Select>
        </VStack>
        <VStack>
          <Text>Välj startgrupp</Text>
          <Select
            minW={100}
            placeholder="Alla"
            onChange={(event) => {
              if (`${filterStartgroupId}` === event.currentTarget.value) {
                setFilterStartgroupId(undefined);
              } else {
                setFilterStartgroupId(parseInt(event.currentTarget.value));
              }
            }}
          >
            {globalContext.competition?.startgroups.map((c) => {
              return (
                <option
                  key={c.id}
                  value={c.id}
                  selected={c.starttime_planned === filterStartgroupId}
                >
                  {timeInMsToString(c.starttime_planned)}
                </option>
              );
            })}
          </Select>
        </VStack>
        <VStack>
          <Text>Sök på namn eller startnummer</Text>
          <Input
            minW={100}
            placeholder="Sök startnr eller namn"
            onChange={(event) => {
              if (filterStartnrOrName === event.currentTarget.value) {
                setFilterStartnrOrName(undefined);
              } else {
                setFilterStartnrOrName(event.currentTarget.value);
              }
            }}
          />
        </VStack>
        <VStack>
          <Text>Sök klubb eller lag namn</Text>
          <Input
            minW={100}
            placeholder="Sök klubb eller lagnamn"
            onChange={(event) => {
              if (filterStartnrOrName === event.currentTarget.value) {
                setFilterGroupName(undefined);
              } else {
                setFilterGroupName(event.currentTarget.value);
              }
            }}
          />
        </VStack>
      </HStack>
      <Center>
        {disableModifying && (
          <Text textColor={"red"} marginBottom={"40px"}>
            Ändringar av anmälan fungerar ej här än. För stadsloppet-2025 görs
            dessa ändringar på osthammarfriidrott.se
          </Text>
        )}
      </Center>

      <FastTable
        data={filteredParticipants}
        pageHeader={`Anmälda till ${globalContext.competition?.tag ?? "Ingen tävling vald"} ${new Date().toISOString().split('T')[0]}`}
        repeatHeaderRows={3}
        downloadFileName={`anmälda_${globalContext.competition?.tag ?? "Ingen tävling vald"}_${new Date().toISOString().split('T')[0]}`}

        headers={[
          "ID",
          "StartNr",
          "Klass",
          "Löpare",
          "Klubb/Lag",
          "Startgrupp",
        ]} //, "DNS", "DNF"]}
        rowFormat={"1fr 1fr 1fr 4fr 4fr 1fr 1fr"} //1fr 1fr"}
        
        toRowData={(p) => {
          return [
            `${p.id}`,
            `${p.startnumber}`,
            p.disciplineName,
            `${p.firstname} ${p.lastname} (${p.birthYear})`,
            p.clubOrTeam,
            timeInMsToString(p.startgroup?.getTime()),
            //p.dns,
            //p.dnf,
          ];
        }}
        fixedFields={fixedFields as (keyof Participant)[]}
        fixedFieldsDisplayName={fixedDisplayLabels}
        modifiableFields={modifyFields as Array<keyof Participant>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        onChange={async (updatedParticipant, old) => {
          if (disableModifying) {
            return true;
          }

          if (updatedParticipant.startgroupId)
            if (old.startgroupId === updatedParticipant.startgroupId) {
              return true;
            } else {
              const success = await PUT.participantStartgroup(
                updatedParticipant.id,
                updatedParticipant.startgroupId
              );

              if (success) {
                const newStartTime =
                  globalContext.competition?.startgroups.find(
                    (s) => s.id === updatedParticipant.startgroupId
                  )?.starttime_planned;
                updatedParticipant.startgroup = newStartTime
                  ? new Date(newStartTime)
                  : null;
                setParticipants((old) => {
                  return old.map((p) =>
                    p.id === updatedParticipant.id ? updatedParticipant : p
                  );
                });
              }
              return success;
            }
          return false;
        }}
        /*
        onDelete={async (deletedParticipant) => {
          return true;
          console.info(
            "Delete: " +
              deletedParticipant.id +
              ", " +
              deletedParticipant.startnumber +
              ", " +
              deletedParticipant.firstname
          );
          const success = await DELETE.participant(deletedParticipant);
          if (success) {
            setParticipants((old) => {
              return old.filter((p) => p.id != deletedParticipant.id);
            });
          }
          return success;
        }}
          */
      />
    </div>
  );
}
